import {
    ApiParameter,
    request,
    ServiceResult,
} from "@insite/client-framework/Services/ApiService";

const baseUrl = "/api/v1/dynamicforms";

export interface DynamicFormAttachment {
    fileName: string;
    dataUrl: string;
}

export interface DynamicFormSubmitRequest extends ApiParameter {
    fields: Record<string, string | boolean>;
    attachments?: DynamicFormAttachment[];
    recaptchaEnabled?: boolean;
    recaptchaToken?: string;
}

export interface DynamicFormSubmitResponse {
    success: boolean;
    message?: string;
    leadId?: string;
    errors?: string[];
}

/**
 * Submits field values; CMS Field Keys are Salesforce API names. Form Id is LeadSource.
 * File uploads are sent separately in `attachments` (not as Lead fields).
 */
export async function submitDynamicForm(
    formId: string,
    body: DynamicFormSubmitRequest,
): Promise<ServiceResult<DynamicFormSubmitResponse>> {
    try {
        const result = await request<DynamicFormSubmitResponse>(
            `${baseUrl}/${encodeURIComponent(formId)}/submit`,
            "POST",
            { "Content-Type": "application/json" },
            JSON.stringify(body),
            status => (status >= 200 && status < 300) || status === 400,
        );

        const normalized = normalizeSubmitResponse(result);
        if (normalized.success) {
            return {
                successful: true,
                result: normalized,
            };
        }

        return {
            successful: false,
            errorMessage: joinErrorMessage(normalized) || "Submit failed.",
        };
    } catch (error: any) {
        const payload = normalizeSubmitResponse(error?.errorJson ?? error?.responseJson ?? error);
        return {
            successful: false,
            errorMessage: joinErrorMessage(payload) || error?.errorMessage || error?.toString?.() || "Submit failed.",
        };
    }
}

/** Joins API error strings, or falls back to the response message. */
function joinErrorMessage(payload: DynamicFormSubmitResponse) {
    const errors = payload.errors?.filter(Boolean) ?? [];
    if (errors.length) {
        return errors.join("\n");
    }

    return payload.message;
}

/** Normalizes PascalCase or camelCase submit JSON into a consistent response shape. */
function normalizeSubmitResponse(raw: any): DynamicFormSubmitResponse {
    if (!raw || typeof raw !== "object") {
        return { success: false, message: "Submit failed.", errors: [] };
    }

    return {
        success: !!(raw.success ?? raw.Success),
        message: raw.message ?? raw.Message,
        leadId: raw.leadId ?? raw.LeadId,
        errors: raw.errors ?? raw.Errors ?? [],
    };
}
